import { useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { CalendarEvent } from "../types/event";
import { fetchEventList } from "./useEventManager";
import { useDebouncedValue } from "./useDebouncedValue";
import { toDatePart } from "../utils/eventDates";

/**
 * What else is already on while a proposed event runs.
 *
 * Written for the new-event dialog's right-hand panel: someone picking a date
 * should be able to see the clash without leaving the dialog to go and look at
 * the calendar.
 *
 * ## The API already answers the right question
 *
 * `event/admin/`'s date window is an OVERLAP test, not containment — see
 * `event/helpers/date_filters.py` in pymble-apis — so a three-day excursion
 * that started before the window still comes back. That is the whole reason
 * this can be one request over the day span rather than a widened guess: the
 * only client-side work left is narrowing the day to the HOURS asked about.
 *
 * ## Only the DAYS are debounced
 *
 * The request is keyed on the day span; the hours are applied to what came
 * back. So nudging a start time from 10:00 to 10:30 re-filters in memory and
 * the panel answers instantly, and only changing the DATE — where a native
 * date input reports "0002-09-21" on the way to 2026, one distinct day per
 * keystroke — waits for the typing to settle before asking the API.
 *
 * Debouncing the whole window instead was the first version, and it made every
 * time nudge blank the panel for 400ms to re-fetch a day it already held.
 *
 * ## EVERY source, including the ones no filter offers
 *
 * Every source the API holds — `manual`, `edumate`, `clipboard` and `liss`
 * today, and whatever is added next — and deliberately not the calendar rail's
 * current ticks. A clash is a fact about
 * the day, not about what someone happens to have filtered the calendar down
 * to, and the rail's state does not reach the create flow anyway.
 *
 * LISS is the one worth naming. The rail hides Edval timetable and room
 * bookings because there is nothing to DO with one if you found it — see
 * EVENT_CATEGORIES — but that is an argument about editing, not about whether
 * the room is free at two o'clock. For "what else is on", a room booking is
 * among the most relevant answers there is.
 *
 * The cost is volume: a normal school week holds ~1,717 events against the
 * ~172 the rail's default shows, so a midday hour on a school day can return
 * dozens. That is the question being asked, so the panel scrolls rather than
 * narrowing it.
 */

/**
 * Every source, via the API's own opt-out rather than a list of ids.
 *
 * `parse_categories` in the API's `source_filters.py` reads `all` as "no
 * filter", and says why in as many words: a caller "can opt out without having
 * to name every source and gain a new one for free when one is added". Naming
 * the four was the first version and it had two problems — it would silently
 * stop meaning "everything" the day a fifth source appears, and LISS had to be
 * bolted onto `EVENT_CATEGORIES`, which is the CREATE list and must only hold
 * calendars this app can actually make (the API answers a `liss` create with a
 * 422).
 *
 * Not the empty list, which is the opposite instruction: `fetchEventList`
 * short-circuits on it and asks for nothing at all.
 */
const CLASH_CATEGORIES = ["all"];

/** The same choice as one cache-key segment. See the query below. */
const CATEGORY_KEY = CLASH_CATEGORIES.join(",");

/**
 * The widest span worth checking, in days.
 *
 * This is a TRUNCATION guard as much as a usefulness one, and it is why the
 * number came down from 31 when the check widened to every source. Unfiltered,
 * a week holds ~1,717 events and August 2026 alone holds 8,072 — against
 * `fetchEventList`'s `limit=10000`. A month-long window would sit just under
 * that cap and a busy one would cross it, and the endpoint does not say when it
 * has: the list simply comes back short, so the panel would report "nothing
 * else is on" about events it was never sent. A fortnight is ~3,500, clear of
 * the cap with room for the collection to grow.
 *
 * Past this the panel says the range is too wide rather than fetching it.
 */
export const MAX_CLASH_SPAN_DAYS = 14;

/** How long the DATES must hold still before the API is asked. See above. */
const DAY_DEBOUNCE_MS = 400;

/** Whole days between two `YYYY-MM-DD` keys, parsed as UTC so DST cannot skew it. */
function spanOf(from: string, to: string): number {
	const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
	return Number.isNaN(ms) ? 0 : Math.round(ms / 86400000);
}

/**
 * A day key as a Date at MIDDAY.
 *
 * `fetchEventList` serialises its bounds with `toISOString().split("T")[0]`, so
 * a Date at local midnight comes out as the day BEFORE in Sydney (UTC+10/+11) —
 * which quietly drops the last day of the window. Midday local is the same
 * calendar day in UTC for any offset inside ±12, so the day asked for is the
 * day sent.
 */
const atMidday = (key: string): Date => new Date(`${key}T12:00:00`);

/** Does `evt` run at any point inside `[from, to)`? */
function overlaps(evt: CalendarEvent, from: number, to: number): boolean {
	const start = Date.parse(evt.start ?? "");
	if (Number.isNaN(start)) return false;
	const parsedEnd = Date.parse(evt.end ?? "");
	const end = Number.isNaN(parsedEnd) ? start : parsedEnd;
	/* An event with no end, or one stored as a single instant, is a point in
	   the day rather than a span — so it counts when it falls inside the
	   window, which `end > from` would reject for a point sitting exactly on
	   the opening edge. */
	if (end <= start) return start >= from && start < to;
	/* Half-open at both ends: something finishing at 10:00 does not clash with
	   something starting at 10:00. */
	return start < to && end > from;
}

export interface ConcurrentEvents {
	/** Everything running during the window, earliest first. */
	events: CalendarEvent[];
	loading: boolean;
	failed: boolean;
	/** The window is wider than `MAX_CLASH_SPAN_DAYS`; nothing was fetched. */
	tooWide: boolean;
	/** Whole days the window touches, inclusive. 0 when there is no window. */
	spanDays: number;
	/**
	 * Ask the API for the day again, ignoring the cached answer.
	 *
	 * For a change of window that is NOT a change of day, where nothing in the
	 * query key moves and the held answer would otherwise stand for up to
	 * `staleTime` — toggling All day is the one that matters, since it swaps
	 * the whole day for an hour of it or back. Cheap enough at that rate, and
	 * it means a switch flipped after somebody else added an event shows the
	 * event rather than the roll from five minutes ago.
	 *
	 * A no-op while the panel is not asking anything (no date yet, a span past
	 * the cap, a closed dialog, or a day key still settling) — `refetch` runs a
	 * DISABLED query, so without that guard this would fire a request for a
	 * window nothing is going to show, or for an empty day key.
	 */
	refresh: () => void;
}

/**
 * @param start ISO instant the proposed event begins, or "" while unset.
 * @param end   ISO instant it finishes, or "" while unset.
 * @param enabled False to hold the request off entirely — a closed dialog.
 */
export function useConcurrentEvents(
	start: string,
	end: string,
	enabled = true,
): ConcurrentEvents {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const fromKey = toDatePart(start);
	/* An end before the start is a half-finished edit, not a window. Falling
	   back to the start day keeps the panel showing that day rather than
	   blanking while someone retypes a date. */
	const rawToKey = toDatePart(end);
	const toKey = rawToKey && rawToKey >= fromKey ? rawToKey : fromKey;

	const settledFrom = useDebouncedValue(fromKey, DAY_DEBOUNCE_MS);
	const settledTo = useDebouncedValue(toKey, DAY_DEBOUNCE_MS);
	/* The days in hand are not the days being asked about yet, so whatever the
	   query holds belongs to a different window. Reported as loading rather
	   than filtered, which would show the wrong day's events as an answer. */
	const settling = settledFrom !== fromKey || settledTo !== toKey;

	const spanDays = fromKey ? spanOf(fromKey, toKey) + 1 : 0;
	const tooWide = spanDays > MAX_CLASH_SPAN_DAYS;
	const active = enabled && isAuthenticated && !!fromKey && !tooWide;
	/* Whether there is a request to make at all. Separate from `active` by the
	   debounce alone: for the first 400ms after the dialog opens the panel is
	   live but the settled key is still "", and `atMidday("")` is an Invalid
	   Date that throws inside `toISOString`. Both `enabled` and `refresh` read
	   this, so neither can fire that request. */
	const canFetch = active && !!settledFrom;

	const query = useQuery({
		/* The sources are IN THE KEY, as they are on the calendar's own list
		   query. They are constant today, so this looks redundant — it is not.
		   What the key omits, react-query cannot tell apart: while the check
		   was still two sources, a day already fetched was served straight back
		   from cache when the list widened to four, so the answer went on being
		   the narrow one for a `staleTime` with no request in the network tab
		   to show why. */
		queryKey: ["event", "concurrent", settledFrom, settledTo, CATEGORY_KEY],
		queryFn: () =>
			fetchEventList(
				instance,
				atMidday(settledFrom),
				atMidday(settledTo),
				CLASH_CATEGORIES,
			),
		/* A day's events do not change while a dialog is open, and the same day
		   is asked for again every time a time is nudged — so the answer is held
		   long enough to make those free. */
		staleTime: 5 * 60 * 1000,
		enabled: canFetch,
	});

	const events = useMemo(() => {
		if (!active || settling) return [];
		const from = Date.parse(start);
		if (Number.isNaN(from)) return [];
		/* Mid-edit the end can be missing, unparseable or earlier than the
		   start. None of those is a window, so the rest of the start day stands
		   in — the same fallback `toKey` makes for the request, so the panel
		   keeps showing that day instead of emptying while someone retypes. */
		const parsedTo = Date.parse(end);
		const to =
			!Number.isNaN(parsedTo) && parsedTo > from
				? parsedTo
				: new Date(from).setHours(23, 59, 59, 999);
		const rows = (query.data?.events ?? []) as CalendarEvent[];
		return rows
			.filter((evt) => overlaps(evt, from, to))
			.sort(
				(a, b) =>
					Date.parse(a.start ?? "") - Date.parse(b.start ?? "") ||
					(a.title ?? "").localeCompare(b.title ?? "", "en-AU"),
			);
	}, [active, settling, query.data, start, end]);

	const { refetch } = query;
	const refresh = useCallback(() => {
		if (canFetch) void refetch();
	}, [canFetch, refetch]);

	return {
		events,
		loading: active && (settling || query.isPending),
		failed: query.isError,
		tooWide,
		spanDays,
		refresh,
	};
}
